import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from './primitives/Button';
import logo from '../assets/litethemelogo.png';

const navLinks = [
  { to: '/', label: 'Home' },
  {
    label: 'Services',
    children: [
      { to: '/services/custom-web-development', label: 'Custom Websites' },
      { to: '/services/mobile-app-development', label: 'Mobile Apps' },
      { to: '/services/ai-integration', label: 'AI & Automation' },
      { to: '/services/email-sms-marketing', label: 'Email & SMS Marketing' },
      { to: '/services/technology-consulting', label: 'Tech Consulting' },
    ],
  },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/insights', label: 'Blog' },
  { to: '/about', label: 'About' },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, isAdmin } = useAuth();

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 shadow-lg bg-white/90 backdrop-blur-md border-b border-gray-200"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img src={logo} alt="Utopian Creations Logo" className="h-8 w-auto" />
              <span className="font-bold text-xl font-sans ml-2 text.heading">Utopian Creations</span>
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              link.children ? (
                <div key={link.label} className="relative group">
                  <Button
                    variant="ghost"
                    className="flex items-center gap-1"
                  >
                    {link.label}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </Button>
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      {link.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className="block px-4 py-2 text-sm text.body hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.to}
                  className="px-3 py-2 rounded-md text-sm font-sans font-medium transition-colors text.body hover:bg-gray-100 hover:text-purple-600"
                >
                  {link.label}
                </Link>
              )
            ))}
            <Link
              to={user ? (isAdmin ? '/admin' : '/dashboard') : '/login'}
              className="px-3 py-2 rounded-md text-sm font-sans font-medium transition-colors text.body hover:bg-gray-100 hover:text-purple-600"
            >
              {user ? (isAdmin ? 'Admin' : 'Client Portal') : 'Client Login'}
            </Link>
            <a
              href="/same-day-launch/"
              className="px-3 py-2 rounded-md text-sm font-sans font-semibold transition-colors text-purple-700 hover:bg-purple-50"
            >
              Same-Day Launch
            </a>
            <Link
              to="/book"
              className="px-3 py-2 rounded-md text-sm font-sans font-medium transition-colors text.body hover:bg-gray-100 hover:text-purple-600"
            >
              Book a call
            </Link>
            <Link
              to="/quote"
              className="ml-2 px-5 py-2 rounded-lg text-sm font-sans font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm"
            >
              Start a project
            </Link>
          </div>
          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              className="group relative w-11 h-11 p-0 border-2 border-purple-600 shadow-sm bg-white hover:bg-purple-50"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((open) => !open)}
            >
              <span
                className={`absolute block w-7 h-0.5 rounded-full transition-all duration-300 bg-purple-600 group-hover:bg-purple-700
                  ${mobileOpen ? 'rotate-45 top-5' : 'top-3'}`}
              ></span>
              <span
                className={`absolute block w-7 h-0.5 rounded-full transition-all duration-300 bg-purple-600 group-hover:bg-purple-700
                  ${mobileOpen ? 'opacity-0' : 'top-5 opacity-100'}`}
              ></span>
              <span
                className={`absolute block w-7 h-0.5 rounded-full transition-all duration-300 bg-purple-600 group-hover:bg-purple-700
                  ${mobileOpen ? '-rotate-45 top-5' : 'top-7'}`}
              ></span>
            </Button>
          </div>
        </div>
        {/* Mobile Dropdown Menu */}
        {mobileOpen && (
          <div 
            className="md:hidden shadow-lg py-2 animate-fade-in rounded-b-lg bg-white/95 border-t border-gray-200"
            role="menu"
            aria-label="Mobile navigation menu"
          >
            <div className="flex flex-col space-y-1 px-2 pb-2" role="menuitemgroup" aria-label="Main navigation items">
              {navLinks.map((link) => (
                link.children ? (
                  <div key={link.label}>
                    <span className="block px-3 py-2 text-sm font-sans font-semibold text.muted uppercase tracking-wide">
                      {link.label}
                    </span>
                    {link.children.map((child) => (
                      <Link
                        key={child.to}
                        to={child.to}
                        className="block px-6 py-2 rounded-md text-sm font-sans font-medium transition-colors text.body hover:bg-purple-50 hover:text-purple-600"
                        onClick={() => setMobileOpen(false)}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={link.label}
                    to={link.to}
                    className="block px-3 py-2 rounded-md text-base font-sans font-medium transition-colors text.body hover:bg-purple-50 hover:text-purple-600"
                    onClick={() => setMobileOpen(false)}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              <Link
                to={user ? (isAdmin ? '/admin' : '/dashboard') : '/login'}
                className="block px-3 py-2 rounded-md text-base font-sans font-medium transition-colors text.body hover:bg-purple-50 hover:text-purple-600"
                onClick={() => setMobileOpen(false)}
              >
                {user ? (isAdmin ? 'Admin' : 'Client Portal') : 'Client Login'}
              </Link>
              <a
                href="/same-day-launch/"
                className="block px-3 py-2 rounded-md text-base font-sans font-semibold transition-colors text-purple-700 hover:bg-purple-50"
              >
                Same-Day Launch
              </a>
              <Link
                to="/book"
                className="block px-3 py-2 rounded-md text-base font-sans font-medium transition-colors text.body hover:bg-purple-50 hover:text-purple-600"
                onClick={() => setMobileOpen(false)}
              >
                Book a call
              </Link>
              <Link
                to="/quote"
                className="block mx-3 mt-2 text-center py-2.5 rounded-lg text-base font-sans font-semibold bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Start a project
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
